import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { ChartBindingObject, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { IShellService } from "@tylertech/incode-module";
import
{
    ExecutionResult,
    ICardData,
    IChartCriteria,
    ProfileSettingViewModel,
} from "@tylertech/tyler-api-types";

import { BillingStatus, UBBillingCyclePeriodDTO } from "../../interfaces";
import { UBDashboardService } from "../../services";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { HttpErrorResponse } from "@angular/common/http";

@Component({
    selector: "utilities-bill-cycle-chart",
    templateUrl: "./billCycleChart.tmpl.html",
    styleUrls: [
        "./billCycleChart.component.scss"
    ],
    standalone: false
})
export class UtilitiesBillCycleChartComponent implements OnInit, OnDestroy, ICustomViewComponent<ChartBindingObject>
{
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $shellService = inject(IShellService);
    private readonly $ubDashboardService = inject(UBDashboardService);

    private readonly destructor$ = new Subject<void>();
    private readonly billingCyclesSubject = new BehaviorSubject<Array<UBBillingCyclePeriodDTO>>([]);
    @Input("chartId") private readonly _chartId: string;

    readonly customDataBinding: ChartBindingObject;
    readonly formGroup: FormGroup;

    private _chartCriteria: IChartCriteria;

    meterReadingData: ICardData;
    billingData: ICardData;
    pastDueData: ICardData[];
    selectedStep: number;
    isLoaded: boolean;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });


        this.formGroup = new FormGroup({
            billingCycle: LgdFormControlFactory.select<UBBillingCyclePeriodDTO>(
                undefined,
                false,
                {
                    displayValueFn: x => x.BillPeriodDescription
                }),
        });
    }

    get chartId(): string | undefined { return this._chartId || this.customDataBinding?.chartId; }

    @Input()
    get chartCriteria(): IChartCriteria | undefined { return this._chartCriteria || this.customDataBinding?.chartCriteria; }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    private get billingCycleControl(): AbstractControl<UBBillingCyclePeriodDTO> { return this.formGroup.get("billingCycle"); }

    get billingCyclesObservable(): Observable<unknown[]> { return this.billingCyclesSubject.asObservable(); }

    get isMeterReadingComplete(): boolean { return this.billingCycleControl.value?.BillingStatus === BillingStatus.MeterReading; }
    get isBillingComplete(): boolean { return this.billingCycleControl.value?.BillingStatus === BillingStatus.Billing; }
    get isPenaltyComplete(): boolean { return this.billingCycleControl.value?.BillingStatus === BillingStatus.Penalty; }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        this.loadCycles();
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData)
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });

        subscribeToValueChanges(this.billingCycleControl, (_newValue: UBBillingCyclePeriodDTO) =>
        {
            this.loadChartDataWithCycle();
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    get hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData)
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private updateChartConfiguration(): void
    {
        if (!this.chartCriteria?.ChartDatas)
        {
            return;
        }

        this.meterReadingData = this.chartCriteria.ChartDatas.find(chartData => chartData.SetLabel === "MeterReadingData").Data[0];
        this.billingData = this.chartCriteria.ChartDatas.find(chartData => chartData.SetLabel === "BillingData").Data[0];
        this.pastDueData = this.chartCriteria.ChartDatas.find(chartData => chartData.SetLabel === "PastDueData").Data[0];
    }

    loadCycles(): void
    {
        this.selectedStep = null;
        this.$ubDashboardService.getRecentBillingCyclePeriods(3)
            .then(result =>
            {
                this.billingCyclesSubject.next(result);

                this.billingCycleControl.setValue(result[0], { emitEvent: false });

                this.loadChartDataWithCycle();
            });
    }

    private loadChartDataWithCycle(): void
    {
        if (!this.billingCycleControl?.value)
            return;

        this.selectedStep = this.billingCycleControl.value.BillingStatus;
        const setting: ProfileSettingViewModel = {
            Caption: "",
            Category: "",
            DataType: "Select",
            Id: "SelectedBillingCyclePeriod",
            Options: undefined,
            Value: this.billingCycleControl.value,
            Hidden: false,
            AdditionalAttributes: undefined
        };

        const index = this.chartCriteria?.Parameters?.findIndex(param => param.Id === "SelectedBillingCyclePeriod");
        if (index !== -1)
        {
            this.chartCriteria?.Parameters?.splice(index, 1);
        }
        this.chartCriteria?.Parameters?.push(setting);
        this.loadChartData();
    }

    isLinkable(current: ICardData): boolean
    {
        return this.$shellService.isEmbedded() && !!current.Title && !!current.TitleActionValue;
    }

    getAriaLabel(current: ICardData): string
    {
        return `OpenShellFunction-${current.Title}-BillingPeriods-UtilityBilling`;
    }

    launch(current: ICardData): void
    {
        this.isLoaded = false;
        this.$shellService.executeShellFunction(current.Title, current.Id, true)
            .catch(
                (result: ExecutionResult<unknown>) =>
                {
                    return this.$commonDialogsService.showAlertMessage(
                        `${current.Title} ${result.Result}`,
                        "Error", "Error");
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    onStepSelect(event: CustomEvent<number>): void
    {
        this.selectedStep = event.detail;
    }
}
